/* Paldaecom theme — vlastní JS (bez závislostí). */
(() => {
  const moneyFormat = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'CZK';
  const fmt = (cents) =>
    new Intl.NumberFormat(document.documentElement.lang || 'cs', { style: 'currency', currency: moneyFormat }).format(cents / 100);

  /* ---------- Reveal + kruhy statistik ---------- */
  const io = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
        });
      }, { rootMargin: '0px 0px -8% 0px' })
    : null;
  const observeReveal = (root = document) =>
    root.querySelectorAll('.pe-reveal:not(.is-in)').forEach((el) => (io ? io.observe(el) : el.classList.add('is-in')));
  observeReveal();
  document.addEventListener('shopify:section:load', (e) => observeReveal(e.target));

  /* ---------- Videa: přehrát jen když jsou vidět ---------- */
  if ('IntersectionObserver' in window) {
    const vio = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        const v = e.target;
        if (e.isIntersecting) { v.preload = 'auto'; v.play().catch(() => {}); } else v.pause();
      });
    }, { threshold: 0.35 });
    document.querySelectorAll('video[data-autoplay]').forEach((v) => vio.observe(v));
  }

  /* ---------- Carousel ---------- */
  class PeCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;
      const step = (dir) => {
        const item = this.track.children[0];
        const w = item ? item.getBoundingClientRect().width + 14 : this.track.clientWidth;
        this.track.scrollBy({ left: dir * w, behavior: 'smooth' });
      };
      this.querySelectorAll('[data-prev]').forEach((b) => b.addEventListener('click', () => step(-1)));
      this.querySelectorAll('[data-next]').forEach((b) => b.addEventListener('click', () => step(1)));
      const update = () => {
        const max = this.track.scrollWidth - this.track.clientWidth - 2;
        this.querySelectorAll('[data-prev]').forEach((b) => (b.disabled = this.track.scrollLeft <= 2));
        this.querySelectorAll('[data-next]').forEach((b) => (b.disabled = this.track.scrollLeft >= max));
      };
      this.track.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
      window.addEventListener('resize', update);
      update();
    }
  }
  customElements.get('pe-carousel') || customElements.define('pe-carousel', PeCarousel);

  /* ---------- Před / po ---------- */
  class PeBeforeAfter extends HTMLElement {
    connectedCallback() {
      const r = this.querySelector('.pe-ba__range');
      if (r) r.addEventListener('input', () => this.style.setProperty('--pe-pos', r.value + '%'));
    }
  }
  customElements.get('pe-before-after') || customElements.define('pe-before-after', PeBeforeAfter);

  /* ---------- Produkt ---------- */
  class PeProduct extends HTMLElement {
    connectedCallback() {
      this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
      this.form = this.querySelector('[data-pe-form]');
      this.idInput = this.querySelector('[data-variant-id]');
      this.addBtn = this.querySelector('[data-add]');
      this.initGallery();
      this.initOptions();
      this.initQty();
      this.initSticky();
      if (this.form) this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }

    get variant() {
      return this.variants.find((v) => String(v.id) === this.idInput.value) || this.variants[0];
    }

    initGallery() {
      const track = this.querySelector('[data-gallery-track]');
      if (!track) return;
      const thumbs = [...this.querySelectorAll('.pe-gallery__thumb')];
      const slides = [...track.children];
      this.goTo = (i) => {
        i = Math.max(0, Math.min(slides.length - 1, i));
        track.scrollTo({ left: slides[i].offsetLeft - track.offsetLeft, behavior: 'smooth' });
      };
      const current = () => Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
      thumbs.forEach((t) => t.addEventListener('click', () => this.goTo(+t.dataset.index)));
      this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.goTo(current() - 1));
      this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.goTo(current() + 1));
      track.addEventListener('scroll', () => {
        const i = current();
        thumbs.forEach((t, n) => t.classList.toggle('is-active', n === i));
        const active = thumbs[i];
        if (active) active.parentElement.scrollTo({ left: active.offsetLeft - 60, behavior: 'smooth' });
      }, { passive: true });
      this.slides = slides;
    }

    initOptions() {
      this.fieldsets = [...this.querySelectorAll('.pe-option')];
      this.fieldsets.forEach((fs) =>
        fs.querySelectorAll('.pe-pill').forEach((btn) =>
          btn.addEventListener('click', () => {
            fs.querySelectorAll('.pe-pill').forEach((b) => b.classList.toggle('is-active', b === btn));
            fs.querySelector('[data-option-label]').textContent = btn.dataset.value;
            this.onOptionChange();
          })
        )
      );
    }

    onOptionChange() {
      const selected = this.fieldsets.map((fs) => fs.querySelector('.pe-pill.is-active')?.dataset.value);
      const v = this.variants.find((x) => x.options.every((o, i) => o === selected[i]));
      if (!v) { this.setAvailable(false); return; }
      this.idInput.value = v.id;
      this.renderVariant(v);
      const url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      window.history.replaceState({}, '', url);
      if (v.featured_media && this.slides) {
        const i = this.slides.findIndex((s) => s.dataset.mediaId === String(v.featured_media.id));
        if (i >= 0) this.goTo(i);
      }
    }

    renderVariant(v) {
      const price = this.querySelector('[data-price]');
      if (price) {
        let html = `<span class="pe-price__now">${fmt(v.price)}</span>`;
        if (v.compare_at_price > v.price) {
          html += `<s class="pe-price__was">${fmt(v.compare_at_price)}</s>`;
          const badge = price.querySelector('.pe-badge');
          if (badge) {
            const save = fmt(v.compare_at_price - v.price).replace(/[,.]00(?=\D*$)/, '');
            html += `<span class="pe-badge">${badge.querySelector('svg')?.outerHTML || ''}Ušetříte ${save}</span>`;
          }
        }
        price.innerHTML = html;
      }
      const sp = this.querySelector('[data-sticky-price]');
      if (sp) sp.textContent = fmt(v.price);
      this.setAvailable(v.available);
    }

    setAvailable(ok) {
      const stock = this.querySelector('[data-stock]');
      if (stock) {
        stock.classList.toggle('is-out', !ok);
        stock.querySelector('[data-stock-text]').textContent = ok ? stock.dataset.in : stock.dataset.out;
      }
      if (this.addBtn) {
        this.addBtn.disabled = !ok;
        if (!this.addLabel) this.addLabel = this.addBtn.querySelector('[data-add-text]').textContent;
        this.addBtn.querySelector('[data-add-text]').textContent = ok ? this.addLabel : (stock?.dataset.out || '');
      }
    }

    initQty() {
      const input = this.querySelector('[data-qty-input]');
      if (!input) return;
      this.querySelectorAll('[data-qty]').forEach((b) =>
        b.addEventListener('click', () => { input.value = Math.max(1, (+input.value || 1) + +b.dataset.qty); })
      );
    }

    initSticky() {
      const bar = this.querySelector('[data-sticky]');
      if (!bar || !this.addBtn || !('IntersectionObserver' in window)) return;
      let past = false;
      new IntersectionObserver(([e]) => {
        past = !e.isIntersecting && e.boundingClientRect.top < 0;
        bar.classList.toggle('is-visible', past);
        bar.setAttribute('aria-hidden', String(!past));
        bar.querySelector('[data-sticky-add]').tabIndex = past ? 0 : -1;
      }).observe(this.addBtn);
      bar.querySelector('[data-sticky-add]').addEventListener('click', () => {
        if (this.addBtn.disabled) { this.addBtn.scrollIntoView({ behavior: 'smooth', block: 'center' }); return; }
        this.form.requestSubmit ? this.form.requestSubmit(this.addBtn) : this.addBtn.click();
      });
    }

    async onSubmit(e) {
      e.preventDefault();
      if (!this.addBtn || this.addBtn.disabled || this.addBtn.classList.contains('is-loading')) return;
      const err = this.querySelector('[data-error]');
      err.hidden = true;
      this.addBtn.classList.add('is-loading');
      const qty = +(this.querySelector('[data-qty-input]')?.value || 1);
      const items = [{ id: +this.idInput.value, quantity: qty }];
      const addon = this.querySelector('[data-addon]');
      if (addon && addon.checked) items.push({ id: +addon.value, quantity: 1 });
      const drawer = document.querySelector('cart-drawer');
      const body = { items };
      if (drawer) {
        body.sections = drawer.getSectionsToRender().map((s) => s.id);
        body.sections_url = window.location.pathname;
      }
      try {
        const res = await fetch((window.routes?.cart_add_url || '/cart/add') + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message || 'Chyba');
        if (addon) addon.checked = false;
        if (drawer && data.sections) {
          drawer.classList.remove('is-empty');
          drawer.renderContents(data);
        } else {
          window.location.href = window.routes?.cart_url || '/cart';
        }
        if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'pe-product', productVariantId: items[0].id, cartData: data });
        }
      } catch (ex) {
        err.textContent = ex.message;
        err.hidden = false;
      } finally {
        this.addBtn.classList.remove('is-loading');
      }
    }
  }
  customElements.get('pe-product') || customElements.define('pe-product', PeProduct);

  /* ---------- Popup se slevou ---------- */
  class PePopup extends HTMLElement {
    connectedCallback() {
      const key = this.dataset.key;
      let seen = false;
      try { seen = localStorage.getItem(key) === '1'; } catch (e) {}
      const posted = this.querySelector('[data-copy]') && this.querySelector('[data-popup-form]');
      if (seen && !posted && !Shopify.designMode) return;
      const open = () => { this.hidden = false; };
      if (posted || Shopify.designMode) open(); else setTimeout(open, (+this.dataset.delay || 8) * 1000);
      this.querySelectorAll('[data-close]').forEach((b) =>
        b.addEventListener('click', () => {
          this.hidden = true;
          try { localStorage.setItem(key, '1'); } catch (e) {}
        })
      );
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.hidden = true; });
      const copy = this.querySelector('[data-copy-btn]');
      if (copy) copy.addEventListener('click', () => {
        const code = this.querySelector('[data-copy]').dataset.copy;
        navigator.clipboard?.writeText(code);
        copy.lastChild.textContent = ' Zkopírováno';
        try { localStorage.setItem(key, '1'); } catch (e) {}
      });
    }
  }
  customElements.get('pe-popup') || customElements.define('pe-popup', PePopup);
})();
